import React, { useEffect, useState, useCallback, useMemo, useRef, ChangeEvent } from 'react';
import {
  ArrowLeft,
  Save,
  Loader2,
  RotateCw,
  Maximize2,
  Minimize2,
  Upload as UploadIcon,
  Building2,
  Layers,
  Droplets,
  AlertCircle,
  Triangle as TriangleIcon,
  Download,
  Trash2,
  Wand2,
} from 'lucide-react';
import { EntranceArrow } from '@/components/floorplan/EntranceArrow';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { FloorplanStepIndicator } from '@/components/FloorplanStepIndicator';
import { useFloorplanTransform } from '@/hooks/useFloorplanTransform';
import { BrandingCanvasHandles } from '@/components/floorplan/BrandingCanvasHandles';
import {
  renderComposite,
  drawOverlayLayer,
  drawWatermarkLayer,
  drawLogoLayer,
  type CompositeWatermarkInput,
  type CompositeLogoInput,
} from '@/lib/floorplanComposite';
import { fetchBrandingAssets, uploadBrandingAsset, uploadOverlayAsset } from '@/services/brandingService';
import { overlaysForSelection, resolveOverlayUrlForAspect } from '@/lib/overlayVariants';
import { processWatermark } from '@/services/imageProcessing';
import type {
  FloorplanState,
  FloorplanTransform,
  FloorplanArrow,
  AspectRatio,
  Project,
  BrandingAsset,
  LogoPosition,
  WatermarkPosition,
} from '@/types';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Validate if an overlay URL's aspect ratio matches the expected project ratio.
 * Tolerance of 0.05 allows for small rounding differences.
 */
const validateOverlayRatio = (
  overlayUrl: string,
  expectedRatio: AspectRatio
): Promise<{ isValid: boolean; overlayRatio: number }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const overlayRatio = img.naturalWidth / img.naturalHeight;
      const expected = expectedRatio === '1:1' ? 1 : 4 / 3;
      const isValid = Math.abs(overlayRatio - expected) < 0.05;
      resolve({ isValid, overlayRatio });
    };
    img.onerror = () => resolve({ isValid: true, overlayRatio: 1 });
    img.src = overlayUrl;
  });
};

interface FloorplanBrandingViewProps {
  floorplanState: FloorplanState;
  currentProject: Project;
  onSave2DTransform: (t: FloorplanTransform) => void;
  onSave3DTransform: (t: FloorplanTransform) => void;
  onSaveComposite: (variant: '2d' | '3d', blob: Blob) => Promise<void>;
  onSaveArrow2D: (arrow: FloorplanArrow | null) => void;
  onSaveArrow3D: (arrow: FloorplanArrow | null) => void;
  onBack: () => void;
  onSaveAndClose: () => void;
}

export const FloorplanBrandingView: React.FC<FloorplanBrandingViewProps> = ({
  floorplanState,
  currentProject,
  onSave2DTransform,
  onSave3DTransform,
  onSaveComposite,
  onSaveArrow2D,
  onSaveArrow3D,
  onBack,
  onSaveAndClose,
}) => {
  const { user } = useAuth();
  const frameRatio: AspectRatio = (currentProject.aspectRatio ?? '4:3') as AspectRatio;
  const has3D = !!floorplanState.generated3dUrl;

  const [overlays, setOverlays] = useState<BrandingAsset[]>([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [overlayMode, setOverlayMode] = useState<'library' | 'upload'>('library');
  const overlayFileInputRef = useRef<HTMLInputElement>(null);
  const [activeSide, setActiveSide] = useState<'2d' | '3d'>('2d');
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloadingRaw, setIsDownloadingRaw] = useState(false);
  const [isDownloadingBranded, setIsDownloadingBranded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Logo panel state
  const [logos, setLogos] = useState<BrandingAsset[]>([]);
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [logoMode, setLogoMode] = useState<'library' | 'upload'>('library');
  const [logoPosition, setLogoPosition] = useState<LogoPosition>('top-right');
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const selectedLogo = useMemo(
    () => logos.find((l) => l.id === selectedLogoId) ?? null,
    [logos, selectedLogoId]
  );

  // Watermark panel state
  const [watermarks, setWatermarks] = useState<BrandingAsset[]>([]);
  const [selectedWatermarkId, setSelectedWatermarkId] = useState<string | null>(null);
  const [watermarkMode, setWatermarkMode] = useState<'library' | 'upload'>('library');
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>('br');
  const [watermarkPreviewUrl, setWatermarkPreviewUrl] = useState<string | null>(null);
  const watermarkFileInputRef = useRef<HTMLInputElement>(null);

  const selectedWatermark = useMemo(
    () => watermarks.find((w) => w.id === selectedWatermarkId) ?? null,
    [watermarks, selectedWatermarkId]
  );

  // Overlay ratio-mismatch warning
  const [overlayMismatchWarning, setOverlayMismatchWarning] = useState<string | null>(null);

  // Arrow state from floorplan state (convenience aliases)
  const arrow2d = floorplanState.arrow2d;
  const arrow3d = floorplanState.arrow3d;

  // Load overlays (most recent first). Default to the newest upload if available.
  useEffect(() => {
    if (!user) return;
    fetchBrandingAssets(user.id, 'overlay')
      .then((assets) => {
        setOverlays(assets);
        if (assets.length > 0) setSelectedOverlayId(assets[0].id);
      })
      .catch((e) => console.warn('[FloorplanBranding] fetchBrandingAssets:', e));
  }, [user]);

  // Load logos on mount
  useEffect(() => {
    if (!user) return;
    fetchBrandingAssets(user.id, 'logo')
      .then(setLogos)
      .catch((e) => console.warn('[FloorplanBranding] fetchLogos:', e));
  }, [user]);

  // Load watermarks on mount
  useEffect(() => {
    if (!user) return;
    fetchBrandingAssets(user.id, 'watermark')
      .then(setWatermarks)
      .catch((e) => console.warn('[FloorplanBranding] fetchWatermarks:', e));
  }, [user]);

  // Generate watermark preview when selection changes
  useEffect(() => {
    if (!selectedWatermark) {
      setWatermarkPreviewUrl(null);
      return;
    }
    let cancelled = false;
    // processWatermark signature: (url, targetWidth, targetHeight, options)
    // Preview sized at 240x120 for a wide-enough preview; scale controls
    // watermark width relative to targetWidth, opacity is 0-100.
    processWatermark(selectedWatermark.publicUrl, 240, 120, {
      opacity: 70,
      position: watermarkPosition,
      scale: 1,
    })
      .then((canvas) => {
        if (cancelled) return;
        setWatermarkPreviewUrl(canvas.toDataURL());
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[FloorplanBranding] watermark preview failed:', err);
        setWatermarkPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWatermark, watermarkPosition]);

  const overlayUrl = useMemo(() => {
    const selected = overlays.find((a) => a.id === selectedOverlayId) ?? null;
    // Passende Aspekt-Variante des Overlays zum Grundriss-Format (Default 4:3).
    return resolveOverlayUrlForAspect(selected, overlays, frameRatio);
  }, [overlays, selectedOverlayId, frameRatio]);

  // Validate overlay aspect ratio against frame ratio
  useEffect(() => {
    if (!overlayUrl) {
      setOverlayMismatchWarning(null);
      return;
    }
    let cancelled = false;
    validateOverlayRatio(overlayUrl, frameRatio).then(({ isValid, overlayRatio }) => {
      if (cancelled) return;
      if (!isValid) {
        const overlayFormat =
          overlayRatio >= 1.5 ? '16:9' : overlayRatio >= 1.2 ? '4:3' : '1:1';
        setOverlayMismatchWarning(
          `Das Overlay-Format (${overlayFormat}, ${overlayRatio.toFixed(2)}) passt nicht zum Grundriss-Format (${frameRatio}). Es wird gestreckt dargestellt.`
        );
      } else {
        setOverlayMismatchWarning(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [overlayUrl, frameRatio]);

  const handleUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;
      try {
        const asset = await uploadOverlayAsset(user.id, file);
        setOverlays((prev) => [asset, ...prev]);
        setSelectedOverlayId(asset.id);
        setOverlayMode('library');
      } catch (err) {
        console.error('[FloorplanBranding] upload failed:', err);
        alert('Upload fehlgeschlagen.');
      } finally {
        if (overlayFileInputRef.current) overlayFileInputRef.current.value = '';
      }
    },
    [user]
  );

  // Overlay-Builder öffnen und robust hierher (Grundriss-Branding) zurückkehren:
  // Der Builder liest ?return= und navigiert nach dem Speichern dorthin; die
  // Grundriss-Session im Tab-Speicher übersteht den Rundgang, sodass der Flow an
  // dieser Position weiterläuft und das neue Overlay hier auftaucht.
  const handleOpenOverlayBuilder = useCallback(() => {
    const ret = `/app/floorplan/branding?p=${currentProject.id}`;
    window.location.assign(`/app/overlay-builder?return=${encodeURIComponent(ret)}`);
  }, [currentProject.id]);

  const handleLogoUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;
      try {
        const asset = await uploadBrandingAsset(user.id, file, 'logo');
        setLogos((prev) => [asset, ...prev]);
        setSelectedLogoId(asset.id);
        setLogoMode('library');
      } catch (err) {
        console.error('[FloorplanBranding] logo upload failed:', err);
        alert('Logo-Upload fehlgeschlagen.');
      } finally {
        if (logoFileInputRef.current) logoFileInputRef.current.value = '';
      }
    },
    [user]
  );

  const handleWatermarkUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !user) return;
      try {
        const asset = await uploadBrandingAsset(user.id, file, 'watermark');
        setWatermarks((prev) => [asset, ...prev]);
        setSelectedWatermarkId(asset.id);
        setWatermarkMode('library');
      } catch (err) {
        console.error('[FloorplanBranding] watermark upload failed:', err);
        alert('Wasserzeichen-Upload fehlgeschlagen.');
      } finally {
        if (watermarkFileInputRef.current) watermarkFileInputRef.current.value = '';
      }
    },
    [user]
  );

  // Memoize composite inputs so postRender callback has stable refs.
  // Recomputing these on every render would re-trigger the redraw effect in
  // useFloorplanTransform and cause canvas thrashing.
  const watermarkCompositeInput = useMemo<CompositeWatermarkInput | null>(() => {
    if (!selectedWatermark) return null;
    return {
      url: selectedWatermark.publicUrl,
      options: { opacity: 70, position: watermarkPosition, scale: 1 },
    };
  }, [selectedWatermark, watermarkPosition]);

  const logoCompositeInput = useMemo<CompositeLogoInput | null>(() => {
    if (!selectedLogo) return null;
    return { url: selectedLogo.publicUrl, position: logoPosition };
  }, [selectedLogo, logoPosition]);

  // Live composite preview: draws overlay/watermark/logo on top of the
  // floorplan base after useFloorplanTransform completes its own redraw.
  // Shared between 2D and 3D canvases — same branding layers apply to both.
  //
  // NOTE: The entrance arrow (Eingangsdreieck) is intentionally NOT drawn here.
  // During editing it is rendered as a DOM overlay via <EntranceArrow> so it
  // remains interactive. drawArrowLayer is called only in handleSave →
  // renderComposite (Layer 5) to bake it into the final exported PNG.
  const postRender = useCallback(
    async (
      ctx: CanvasRenderingContext2D,
      frame: { width: number; height: number }
    ) => {
      if (overlayUrl) await drawOverlayLayer(ctx, overlayUrl, frame);
      if (watermarkCompositeInput) {
        await drawWatermarkLayer(ctx, watermarkCompositeInput, frame);
      }
      if (logoCompositeInput) await drawLogoLayer(ctx, logoCompositeInput, frame);
    },
    [overlayUrl, watermarkCompositeInput, logoCompositeInput]
  );

  const t2d = useFloorplanTransform({
    imageUrl: floorplanState.generated2dUrl,
    frameRatio,
    initialTransform: floorplanState.transform2d,
    onChange: onSave2DTransform,
    postRender,
  });

  const t3d = useFloorplanTransform({
    imageUrl: floorplanState.generated3dUrl,
    frameRatio,
    initialTransform: floorplanState.transform3d,
    onChange: onSave3DTransform,
    postRender,
  });

  const activeGeneratedUrl = activeSide === '2d'
    ? floorplanState.generated2dUrl
    : floorplanState.generated3dUrl;

  const handleDownloadRaw = useCallback(async () => {
    if (!activeGeneratedUrl) return;
    setIsDownloadingRaw(true);
    try {
      const resp = await fetch(activeGeneratedUrl);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `grundriss-${activeSide}-ohne-branding.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
    } finally {
      setIsDownloadingRaw(false);
    }
  }, [activeGeneratedUrl, activeSide]);

  const handleDownloadBranded = useCallback(async () => {
    if (!activeGeneratedUrl) return;
    setIsDownloadingBranded(true);
    try {
      const activeTransform = activeSide === '2d' ? t2d.transform : t3d.transform;
      const activeArrow = activeSide === '2d' ? arrow2d : arrow3d;
      const blob = await renderComposite({
        imageUrl: activeGeneratedUrl,
        transform: activeTransform,
        frameRatio,
        overlayUrl,
        watermark: watermarkCompositeInput,
        logo: logoCompositeInput,
        arrow: activeArrow,
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `grundriss-${activeSide}-mit-branding.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Download fehlgeschlagen');
    } finally {
      setIsDownloadingBranded(false);
    }
  }, [
    activeGeneratedUrl,
    activeSide,
    t2d.transform,
    t3d.transform,
    arrow2d,
    arrow3d,
    frameRatio,
    overlayUrl,
    watermarkCompositeInput,
    logoCompositeInput,
  ]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      if (floorplanState.generated2dUrl) {
        const blob2d = await renderComposite({
          imageUrl: floorplanState.generated2dUrl,
          transform: t2d.transform,
          frameRatio,
          overlayUrl,
          watermark: watermarkCompositeInput,
          logo: logoCompositeInput,
          arrow: arrow2d,
        });
        await onSaveComposite('2d', blob2d);
      }
      if (has3D && floorplanState.generated3dUrl) {
        const blob3d = await renderComposite({
          imageUrl: floorplanState.generated3dUrl,
          transform: t3d.transform,
          frameRatio,
          overlayUrl,
          watermark: watermarkCompositeInput,
          logo: logoCompositeInput,
          arrow: arrow3d,
        });
        await onSaveComposite('3d', blob3d);
      }
      onSaveAndClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  }, [
    floorplanState.generated2dUrl,
    floorplanState.generated3dUrl,
    has3D,
    t2d.transform,
    t3d.transform,
    frameRatio,
    overlayUrl,
    watermarkCompositeInput,
    logoCompositeInput,
    arrow2d,
    arrow3d,
    onSaveComposite,
    onSaveAndClose,
  ]);

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Branding</h1>
        <div className="w-10" />
      </div>

      <FloorplanStepIndicator currentStep="branding" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardContent className="p-0">
              <Accordion type="multiple" defaultValue={['logo', 'watermark', 'overlay', 'arrow']} className="space-y-0">
                {/* Logo Panel */}
                <AccordionItem value="logo" className="border-b">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      <Building2 className="size-4 stroke-[1.5]" />
                      <span className="font-medium text-sm">Logo</span>
                      {selectedLogo && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLogoId(null);
                          }}
                          className="ml-auto mr-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <Tabs
                      value={logoMode}
                      onValueChange={(v) => setLogoMode(v as 'library' | 'upload')}
                    >
                      <TabsList className="grid grid-cols-2 w-full mb-2">
                        <TabsTrigger value="library">Bibliothek</TabsTrigger>
                        <TabsTrigger value="upload">Upload</TabsTrigger>
                      </TabsList>

                      <TabsContent value="library">
                        {logos.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">
                            Keine gespeicherten Logos. Upload-Tab nutzen.
                          </p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {logos.map((logo) => (
                              <button
                                key={logo.id}
                                type="button"
                                onClick={() => setSelectedLogoId(logo.id)}
                                className={`aspect-square bg-white rounded-md overflow-hidden border transition-all ${
                                  selectedLogoId === logo.id
                                    ? 'border-primary ring-2 ring-primary/30'
                                    : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <img
                                  src={logo.publicUrl}
                                  alt=""
                                  className="w-full h-full object-contain p-1"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="upload">
                        <label className="block border-2 border-dashed border-border rounded-md p-4 text-center cursor-pointer hover:border-primary transition-colors">
                          <UploadIcon className="size-5 mx-auto mb-1 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Datei wählen oder hierher ziehen
                          </span>
                          <input
                            ref={logoFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                      </TabsContent>
                    </Tabs>

                    {selectedLogo && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold mb-1.5">Position</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {(
                            [
                              { pos: 'top-left' as const, label: 'Oben links' },
                              { pos: 'top-right' as const, label: 'Oben rechts' },
                              { pos: 'bottom-left' as const, label: 'Unten links' },
                              { pos: 'bottom-right' as const, label: 'Unten rechts' },
                            ]
                          ).map(({ pos, label }) => (
                            <button
                              key={pos}
                              type="button"
                              onClick={() => setLogoPosition(pos)}
                              className={`text-xs py-1.5 px-2 rounded-md border transition-colors ${
                                logoPosition === pos
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-card border-border hover:border-primary/50'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Watermark Panel */}
                <AccordionItem value="watermark" className="border-b">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      <Droplets className="size-4 stroke-[1.5]" />
                      <span className="font-medium text-sm">Wasserzeichen</span>
                      {selectedWatermark && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWatermarkId(null);
                          }}
                          className="ml-auto mr-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <Tabs
                      value={watermarkMode}
                      onValueChange={(v) => setWatermarkMode(v as 'library' | 'upload')}
                    >
                      <TabsList className="grid grid-cols-2 w-full mb-2">
                        <TabsTrigger value="library">Bibliothek</TabsTrigger>
                        <TabsTrigger value="upload">Upload</TabsTrigger>
                      </TabsList>

                      <TabsContent value="library">
                        {watermarks.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">
                            Keine gespeicherten Wasserzeichen. Upload-Tab nutzen.
                          </p>
                        ) : (
                          <div className="grid grid-cols-3 gap-2">
                            {watermarks.map((wm) => (
                              <button
                                key={wm.id}
                                type="button"
                                onClick={() => setSelectedWatermarkId(wm.id)}
                                className={`aspect-square bg-white rounded-md overflow-hidden border transition-all ${
                                  selectedWatermarkId === wm.id
                                    ? 'border-primary ring-2 ring-primary/30'
                                    : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <img
                                  src={wm.publicUrl}
                                  alt=""
                                  className="w-full h-full object-contain p-1"
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </TabsContent>

                      <TabsContent value="upload">
                        <label className="block border-2 border-dashed border-border rounded-md p-4 text-center cursor-pointer hover:border-primary transition-colors">
                          <UploadIcon className="size-5 mx-auto mb-1 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Datei wählen oder hierher ziehen
                          </span>
                          <input
                            ref={watermarkFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleWatermarkUpload}
                            className="hidden"
                          />
                        </label>
                      </TabsContent>
                    </Tabs>

                    {watermarkPreviewUrl && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold mb-1.5">Vorschau</p>
                        <div className="bg-neutral-100 rounded-md p-2 flex items-center justify-center min-h-[60px]">
                          <img src={watermarkPreviewUrl} alt="Preview" className="max-h-20" />
                        </div>
                      </div>
                    )}

                    {selectedWatermark && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold mb-1.5">Position</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {(
                            [
                              { pos: 'tl' as const, label: 'Oben links' },
                              { pos: 'center' as const, label: 'Mitte' },
                              { pos: 'tr' as const, label: 'Oben rechts' },
                              { pos: 'bl' as const, label: 'Unten links' },
                              { pos: 'tile' as const, label: 'Gekachelt' },
                              { pos: 'br' as const, label: 'Unten rechts' },
                            ]
                          ).map(({ pos, label }) => (
                            <button
                              key={pos}
                              type="button"
                              onClick={() => setWatermarkPosition(pos)}
                              className={`text-[11px] py-1.5 px-1 rounded-md border transition-colors ${
                                watermarkPosition === pos
                                  ? 'bg-primary text-white border-primary'
                                  : 'bg-card border-border hover:border-primary/50'
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Overlay Panel */}
                <AccordionItem value="overlay" className="border-b">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      <Layers className="size-4 stroke-[1.5]" />
                      <span className="font-medium text-sm">Overlay</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <Tabs
                      value={overlayMode}
                      onValueChange={(v) => setOverlayMode(v as 'library' | 'upload')}
                    >
                      <TabsList className="grid grid-cols-2 w-full mb-2">
                        <TabsTrigger value="library">Bibliothek</TabsTrigger>
                        <TabsTrigger value="upload">Upload</TabsTrigger>
                      </TabsList>

                      <TabsContent value="library">
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedOverlayId(null)}
                            className={`aspect-square bg-white rounded-md overflow-hidden border transition-all flex items-center justify-center text-xs text-muted-foreground ${
                              selectedOverlayId === null
                                ? 'border-primary ring-2 ring-primary/30'
                                : 'border-border hover:border-primary/50'
                            }`}
                          >
                            Kein Overlay
                          </button>
                          {overlaysForSelection(overlays).map((ov) => (
                            <button
                              key={ov.id}
                              type="button"
                              onClick={() => setSelectedOverlayId(ov.id)}
                              className={`aspect-square bg-white rounded-md overflow-hidden border transition-all ${
                                selectedOverlayId === ov.id
                                  ? 'border-primary ring-2 ring-primary/30'
                                  : 'border-border hover:border-primary/50'
                              }`}
                              title={ov.name}
                            >
                              <img
                                src={ov.publicUrl}
                                alt=""
                                className="w-full h-full object-contain p-1"
                              />
                            </button>
                          ))}
                        </div>
                        {overlaysForSelection(overlays).length === 0 && (
                          <p className="text-xs text-muted-foreground pt-2 text-center">
                            Keine gespeicherten Overlays. Upload-Tab nutzen.
                          </p>
                        )}
                      </TabsContent>

                      <TabsContent value="upload">
                        <label className="block border-2 border-dashed border-border rounded-md p-4 text-center cursor-pointer hover:border-primary transition-colors">
                          <UploadIcon className="size-5 mx-auto mb-1 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            Datei wählen oder hierher ziehen
                          </span>
                          <input
                            ref={overlayFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleUpload}
                            className="hidden"
                          />
                        </label>
                      </TabsContent>
                    </Tabs>

                    {overlayMismatchWarning && (
                      <div className="mt-2 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md p-2 text-xs text-orange-900">
                        <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                        <span>{overlayMismatchWarning}</span>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleOpenOverlayBuilder}
                      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                    >
                      <Wand2 className="size-3.5" />
                      Neues Overlay erstellen
                    </button>
                  </AccordionContent>
                </AccordionItem>

                {/* Arrow Panel */}
                <AccordionItem value="arrow" className="border-b-0">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      <TriangleIcon className="size-4 stroke-[1.5]" />
                      <span className="font-medium text-sm">Eingangsdreieck</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Setzt ein klassisches Eingangsdreieck auf dem {activeSide === '2d' ? '2D' : '3D'}-Grundriss, das den Wohnungseingang markiert. Das Dreieck erscheint oben rechts und kann direkt im Bild verschoben, vergrößert/verkleinert und gedreht werden.
                    </p>

                    {!(activeSide === '2d' ? arrow2d : arrow3d) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const defaultArrow: FloorplanArrow = {
                            x: 0.88,
                            y: 0.12,
                            size: 0.08,
                            rotation: 0,
                          };
                          if (activeSide === '2d') onSaveArrow2D(defaultArrow);
                          else onSaveArrow3D(defaultArrow);
                        }}
                      >
                        Eingangsdreieck hinzufügen
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-xs text-muted-foreground italic">
                          Eingangsdreieck aktiv. Direkt im Bild ziehen, skalieren (Eckgriffe) oder drehen (Griff unterhalb).
                        </p>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            if (activeSide === '2d') onSaveArrow2D(null);
                            else onSaveArrow3D(null);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eingangsdreieck entfernen
                        </Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Preview column */}
        <div className="lg:col-span-2 space-y-6">
          <div className={`grid gap-6 ${has3D ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
            <CanvasPane
              label="2D"
              isActive={activeSide === '2d'}
              onClick={() => setActiveSide('2d')}
              canvasRef={t2d.canvasRef}
              isReady={t2d.isReady}
              onFit={t2d.fit}
              onRotate90={() => t2d.update({ rotation: t2d.transform.rotation + 90 })}
              onReset={t2d.reset}
              transform={t2d.transform}
              update={t2d.update}
              handlers={t2d.handlers}
              applyCornerScale={t2d.applyCornerScale}
              applyRotation={t2d.applyRotation}
              imageNaturalSize={t2d.imageNaturalSize}
              frame={t2d.frame}
              arrow={arrow2d}
              onArrowChange={onSaveArrow2D}
            />
            {has3D && (
              <CanvasPane
                label="3D"
                isActive={activeSide === '3d'}
                onClick={() => setActiveSide('3d')}
                canvasRef={t3d.canvasRef}
                isReady={t3d.isReady}
                onFit={t3d.fit}
                onRotate90={() => t3d.update({ rotation: t3d.transform.rotation + 90 })}
                onReset={t3d.reset}
                transform={t3d.transform}
                update={t3d.update}
                handlers={t3d.handlers}
                applyCornerScale={t3d.applyCornerScale}
                applyRotation={t3d.applyRotation}
                imageNaturalSize={t3d.imageNaturalSize}
                frame={t3d.frame}
                arrow={arrow3d}
                onArrowChange={onSaveArrow3D}
              />
            )}
          </div>

        </div>
      </div>

      {saveError && (
        <div className="text-sm text-destructive text-center">{saveError}</div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleDownloadRaw}
          disabled={!activeGeneratedUrl || isDownloadingRaw}
        >
          {isDownloadingRaw
            ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
            : <Download className="w-4 h-4 mr-2" />}
          Grundriss ohne Branding
        </Button>
        <Button
          variant="outline"
          onClick={handleDownloadBranded}
          disabled={!activeGeneratedUrl || isDownloadingBranded}
        >
          {isDownloadingBranded
            ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
            : <Download className="w-4 h-4 mr-2" />}
          Grundriss mit Branding
        </Button>
        <Button variant="outline" onClick={onBack}>Zurück</Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern & Fertig
        </Button>
      </div>
    </div>
  );
};

type CanvasElementId = 'grundriss' | 'eingangsdreieck' | null;

interface CanvasPaneProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isReady: boolean;
  onFit: () => void;
  onRotate90: () => void;
  onReset: () => void;
  transform: FloorplanTransform;
  update: (patch: Partial<FloorplanTransform>) => void;
  handlers: {
    onPointerDown: (e: React.PointerEvent<HTMLCanvasElement>) => void;
    onWheel: (e: React.WheelEvent<HTMLCanvasElement>) => void;
  };
  applyCornerScale: (args: { scaleFactor: number; startScale: number }) => void;
  applyRotation: (args: { angleDeg: number }) => void;
  imageNaturalSize: { width: number; height: number } | null;
  frame: { width: number; height: number };
  arrow: FloorplanArrow | null;
  onArrowChange: (next: FloorplanArrow | null) => void;
}

const CanvasPane: React.FC<CanvasPaneProps> = ({
  label,
  isActive,
  onClick,
  canvasRef,
  isReady,
  onFit,
  onRotate90,
  onReset,
  transform,
  update,
  handlers,
  applyCornerScale,
  applyRotation,
  imageNaturalSize,
  frame,
  arrow,
  onArrowChange,
}) => {
  // Per-pane selection state. Only one element can be selected at a time on a
  // given canvas. Click on the canvas selects 'grundriss', clicking on the
  // entrance triangle selects 'eingangsdreieck' (via stopPropagation in its
  // own handler), clicking the card chrome deselects, and Escape deselects.
  const [selectedElement, setSelectedElement] = useState<CanvasElementId>(null);

  // Escape clears the selection — standard editor convention.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedElement(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Wrap the canvas pan-pointer-down so a click on the grundriss also selects it.
  const handleCanvasPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      setSelectedElement('grundriss');
      handlers.onPointerDown(e);
    },
    [handlers]
  );

  return (
    <Card
      onClick={onClick}
      className={`cursor-pointer transition-all ${isActive ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100'}`}
    >
      <CardContent
        className="p-4 space-y-3"
        // Clicks on the card chrome (not on canvas/handles, which stopPropagation)
        // deselect — gives the user a forgiving way to drop selection.
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) setSelectedElement(null);
        }}
      >
        <div className="flex items-center justify-between">
          <span className={`text-sm font-semibold ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
            {label} {isActive && '(aktiv)'}
          </span>
        </div>

        <div className="relative bg-muted/20 rounded overflow-hidden aspect-[4/3]">
          {isReady ? (
            <>
              <canvas
                ref={canvasRef}
                className="w-full h-full object-contain touch-none cursor-grab active:cursor-grabbing"
                onPointerDown={handleCanvasPointerDown}
                onWheel={handlers.onWheel}
              />
              <BrandingCanvasHandles
                canvasRef={canvasRef}
                transform={transform}
                imageNaturalSize={imageNaturalSize}
                frame={frame}
                onDragBody={(dx, dy) =>
                  update({
                    tx: clamp(transform.tx + dx, 0, 1),
                    ty: clamp(transform.ty + dy, 0, 1),
                  })
                }
                onCornerScale={applyCornerScale}
                onRotate={applyRotation}
                isSelected={selectedElement === 'grundriss'}
                onSelect={() => setSelectedElement('grundriss')}
              />
              {arrow && (
                <EntranceArrow
                  canvasRef={canvasRef}
                  frame={frame}
                  arrow={arrow}
                  onChange={onArrowChange}
                  isSelected={selectedElement === 'eingangsdreieck'}
                  onSelect={() => setSelectedElement('eingangsdreieck')}
                />
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onFit(); }}>
            <Maximize2 className="w-3 h-3 mr-1" /> Einpassen
          </Button>
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onRotate90(); }}>
            <RotateCw className="w-3 h-3 mr-1" /> 90°
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onReset(); }}>
            <Minimize2 className="w-3 h-3 mr-1" /> Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
